import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { useApiList, useApiOne, useMutation } from "../api/hooks";
import { Badge, DataTable, Field, fmtDate, Modal, Money, Page, Pagination, StatusBadge, Toolbar, useToast } from "../components/ui";
import type { Order } from "../api/types";

const STATUSES = ["scheduled", "pending", "preparing", "ready", "enroute", "delivered", "cancelled", "failed"];

/** All orders across vendors (`/vendor/orders` is unscoped for admins). */
export function OrdersPage() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") ?? "all";
  const status = params.get("status") ?? "";
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const query = useMemo(() => ({ type, status: status || undefined, page, per_page: 25 }), [type, status, page]);
  const list = useApiList<Order>("/vendor/orders", query);

  return (
    <Page title="Orders">
      <Toolbar>
        <select className="input" value={type} onChange={(e) => (setParams({ type: e.target.value, status }), setPage(1))}>
          <option value="all">All orders</option>
          <option value="active">Active</option>
          <option value="history">History</option>
        </select>
        <select className="input" value={status} onChange={(e) => (setParams({ type, status: e.target.value }), setPage(1))}>
          <option value="">Status: any</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Toolbar>
      <DataTable
        columns={[
          { key: "code", label: "Code", render: (o) => <span className="mono">#{o.code}</span> },
          { key: "placed_at", label: "Placed", render: (o) => fmtDate(o.placed_at) },
          { key: "vendor", label: "Vendor", render: (o) => o.vendor.name },
          { key: "customer", label: "Customer", render: (o) => o.customer.name },
          { key: "driver", label: "Driver", render: (o) => o.driver?.name ?? "—" },
          { key: "payment", label: "Payment", render: (o) => `${o.payment_method.slug} · ${o.payment_status}` },
          { key: "total", label: "Total", align: "right", render: (o) => <Money value={o.total} /> },
          { key: "status", label: "Status", render: (o) => <StatusBadge status={o.status} /> },
        ]}
        rows={list.data}
        loading={list.loading}
        error={list.error}
        onRowClick={(o) => navigate(`/orders/${o.id}`)}
      />
      <Pagination page={page} perPage={25} total={list.total} onPage={setPage} />
    </Page>
  );
}

export function OrderDetailPage() {
  const { id = "" } = useParams();
  const { data: o, loading, error, reload } = useApiOne<Order>(`/vendor/orders/${id}`);
  const { run, busy } = useMutation();
  const toast = useToast();
  const [assigning, setAssigning] = useState(false);
  const [reasonFor, setReasonFor] = useState<null | "cancelled" | "failed" | "reject-payment">(null);
  const [reason, setReason] = useState("");

  async function act(path: string, body?: unknown, done = "Done") {
    const r = await run(path, { method: "POST", body });
    if (r.ok) {
      toast(done);
      reload();
    } else toast(r.error, "err");
  }
  const transition = (status: string, why?: string) => act(`/vendor/orders/${id}/transition`, { status, reason: why }, `Order ${status}`);

  if (error) return <p className="error">{error}</p>;
  if (!o) return <p>{loading ? "Loading…" : ""}</p>;

  const next: { label: string; status: string; danger?: boolean; needsReason?: boolean }[] = (() => {
    switch (o.status) {
      case "scheduled":
      case "pending":
        return [
          { label: "Accept", status: "preparing" },
          { label: "Cancel", status: "cancelled", danger: true, needsReason: true },
        ];
      case "preparing":
        return [
          { label: "Mark ready", status: "ready" },
          { label: "Cancel", status: "cancelled", danger: true, needsReason: true },
        ];
      case "ready":
        return [
          { label: o.is_pickup ? "Handed over" : "Out for delivery", status: "enroute" },
          { label: "Cancel", status: "cancelled", danger: true, needsReason: true },
        ];
      case "enroute":
        return [
          { label: "Delivered", status: "delivered" },
          { label: "Failed", status: "failed", danger: true, needsReason: true },
        ];
      default:
        return [];
    }
  })();

  return (
    <Page
      title={`Order #${o.code}`}
      actions={
        <>
          {o.payment_status === "review" && (
            <>
              <button type="button" className="btn btn-ghost danger" disabled={busy} onClick={() => setReasonFor("reject-payment")}>
                Reject payment
              </button>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(`/vendor/orders/${id}/payment/approve`, undefined, "Payment approved")}>
                Approve payment
              </button>
            </>
          )}
          {!o.is_pickup && ["pending", "preparing", "ready"].includes(o.status) && (
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setAssigning(true)}>
              {o.driver ? "Reassign driver" : "Assign driver"}
            </button>
          )}
          {next.map((n) => (
            <button
              key={n.status}
              type="button"
              className={`btn ${n.danger ? "btn-ghost danger" : "btn-primary"}`}
              disabled={busy}
              onClick={() => (n.needsReason ? setReasonFor(n.status as "cancelled" | "failed") : transition(n.status))}
            >
              {n.label}
            </button>
          ))}
        </>
      }
    >
      <div className="grid">
        <section className="card">
          <h2>
            <StatusBadge status={o.status} /> <span className="muted">{fmtDate(o.placed_at)}</span>
          </h2>
          <dl>
            <dt>Vendor</dt>
            <dd>{o.vendor.name}</dd>
            <dt>Customer</dt>
            <dd>
              {o.customer.name} {o.customer.phone && <span className="muted">· {o.customer.phone}</span>}
            </dd>
            <dt>Driver</dt>
            <dd>{o.driver ? `${o.driver.name}${o.driver.phone ? ` · ${o.driver.phone}` : ""}` : "—"}</dd>
            <dt>{o.is_pickup ? "Fulfilment" : "Deliver to"}</dt>
            <dd>{o.is_pickup ? "Customer pickup" : (o.address?.address ?? "—")}</dd>
            <dt>Payment</dt>
            <dd>
              {o.payment_method.slug} · <Badge tone={o.payment_status === "successful" ? "ok" : o.payment_status === "failed" ? "bad" : "warn"}>{o.payment_status}</Badge>
            </dd>
            {o.note && (
              <>
                <dt>Note</dt>
                <dd>{o.note}</dd>
              </>
            )}
            {o.cancel_reason && (
              <>
                <dt>Cancel reason</dt>
                <dd>{o.cancel_reason}</dd>
              </>
            )}
          </dl>
        </section>
        <section className="card">
          <h2>Items</h2>
          <table className="table table-plain">
            <tbody>
              {o.lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    {l.qty} × {l.product_name}
                    {l.options_text && <div className="muted">{l.options_text}</div>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Money value={l.line_total} />
                  </td>
                </tr>
              ))}
              <tr className="sep">
                <td>Subtotal</td>
                <td style={{ textAlign: "right" }}>
                  <Money value={o.sub_total} />
                </td>
              </tr>
              {Number(o.discount) > 0 && (
                <tr>
                  <td>Discount</td>
                  <td style={{ textAlign: "right" }}>
                    <Money value={-Number(o.discount)} />
                  </td>
                </tr>
              )}
              <tr>
                <td>Delivery fee</td>
                <td style={{ textAlign: "right" }}>
                  <Money value={o.delivery_fee} />
                </td>
              </tr>
              <tr>
                <td>Tax</td>
                <td style={{ textAlign: "right" }}>
                  <Money value={o.tax} />
                </td>
              </tr>
              {Number(o.tip) > 0 && (
                <tr>
                  <td>Tip</td>
                  <td style={{ textAlign: "right" }}>
                    <Money value={o.tip} />
                  </td>
                </tr>
              )}
              <tr className="total">
                <td>Total</td>
                <td style={{ textAlign: "right" }}>
                  <Money value={o.total} />
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <section className="card">
          <h2>Timeline</h2>
          <ul className="timeline">
            {[...o.events].reverse().map((e, i) => (
              <li key={i}>
                <StatusBadge status={e.status} /> <span className="muted">by {e.actor_kind}</span> · {fmtDate(e.at)}
                {e.reason && <div className="muted">{e.reason}</div>}
              </li>
            ))}
          </ul>
        </section>
      </div>

      {assigning && (
        <AssignDriverModal
          vendorId={o.vendor.id}
          onClose={() => setAssigning(false)}
          onPick={async (driverId) => {
            setAssigning(false);
            await act(`/vendor/orders/${id}/assign`, { driver_id: driverId }, "Driver assigned");
          }}
        />
      )}
      {reasonFor && (
        <Modal title={reasonFor === "reject-payment" ? "Reject payment proof" : `Mark order ${reasonFor}`} onClose={() => setReasonFor(null)}>
          <Field label="Reason (shown to the customer)">
            <input className="input" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setReasonFor(null)}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy || reason.trim().length < 2}
              onClick={async () => {
                const why = reason.trim();
                const which = reasonFor;
                setReasonFor(null);
                setReason("");
                if (which === "reject-payment") await act(`/vendor/orders/${id}/payment/reject`, { reason: why }, "Payment rejected");
                else await transition(which, why);
              }}
            >
              Confirm
            </button>
          </div>
        </Modal>
      )}
    </Page>
  );
}

function AssignDriverModal({ vendorId, onClose, onPick }: { vendorId: string; onClose: () => void; onPick: (driverId: string) => void }) {
  const [q, setQ] = useState("");
  const all = useApiList<{ id: string; name: string; phone: string | null; is_online: boolean; vendor_driver: boolean; active_orders: number; distance_km: string | null }>(
    "/vendor/drivers",
    { vendor_id: vendorId },
  );
  const rows = all.data.filter((d) => !q || d.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal title="Assign a driver" onClose={onClose}>
      <input className="input" placeholder="Filter drivers…" value={q} onChange={(e) => setQ(e.target.value)} />
      <DataTable
        columns={[
          { key: "name", label: "Driver", render: (d) => (<>{d.name}{d.vendor_driver && <Badge tone="info">vendor</Badge>}</>) },
          { key: "phone", label: "Phone", render: (d) => d.phone ?? "—" },
          { key: "is_online", label: "Online", render: (d) => <Badge tone={d.is_online ? "ok" : "neutral"}>{d.is_online ? "online" : "offline"}</Badge> },
          { key: "distance_km", label: "Distance", align: "right", render: (d) => (d.distance_km ? `${d.distance_km} km` : "—") },
          { key: "active_orders", label: "Active", align: "right" },
        ]}
        rows={rows}
        loading={all.loading}
        error={all.error}
        onRowClick={(d) => onPick(d.id)}
      />
    </Modal>
  );
}
